import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Checkbox,
  IconButton,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  Menu,
  OutlinedInput,
  FormControl,
  InputLabel,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useMutation, useQuery } from '@apollo/client';
import CustomDialog from '../useCloseConfirmation';
import { ShortText, FormatAlignLeft, RadioButtonChecked, CheckBox, ArrowDropDown, Dialpad, Delete, Add } from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CancelIcon from '@mui/icons-material/Cancel';
import { Link } from 'react-router-dom';
import { useBookingTypeContext } from '../bookingTypeProvider';
import { GET_INVITEE_ANSWERS, GET_INVITEE_QUESTIONS } from '../../../../graphql/queries';
import { ADD_INVITEE_QUESTION, DELETE_INVITEE_QUESTION } from '../../../../graphql/mutations';

const InviteeQuestions = () => {

  const {
    showInviteeQuestions,
    setShowInviteeQuestions,
    bookingType,
    combinedQuestions, refetch } = useBookingTypeContext();


  const types = [
    { text: 'One Line', icon: <ShortText />, placeholder: 'Enter a brief answer' },
    { text: 'Multiple Lines', icon: <FormatAlignLeft />, placeholder: 'Enter your response' },
    { text: 'Radio Buttons', icon: <RadioButtonChecked />, options: ['Option 1', 'Option 2'] },
    { text: 'Checkboxes', icon: <CheckBox />, options: ['Check 1', 'Check 2'] },
    { text: 'Drop down', icon: <ArrowDropDown />, options: ['Select One', 'Option 1', 'Option 2'] },
    { text: 'Phone Number', icon: <Dialpad />, placeholder: 'Enter your phone number' },
  ];
  const defaultBookingTypeState = () => ({
    id: null,
    title: '',
    type: '',
    required: false,
    options: [],
    bookingTypeId: parseInt(bookingType?.id),
  });

  const [items, setItems] = useState(combinedQuestions || []);
  const [currentItem, setCurrentItem] = useState(defaultBookingTypeState);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [currentUpdateMode, setCurrentUpdateMode] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const { data: answerData } = useQuery(GET_INVITEE_ANSWERS, { variables: { bookingTypeId: parseInt(bookingType?.id) } });

  const [addInviteeQuestion] = useMutation(ADD_INVITEE_QUESTION, { refetchQueries: [{ query: GET_INVITEE_QUESTIONS }] });
  const [deleteInviteeQuestion] = useMutation(DELETE_INVITEE_QUESTION, { refetchQueries: [{ query: GET_INVITEE_QUESTIONS }] });
  const [answers, setAnswers] = useState([]);
  const [orginalAnswers, setOrginalAnswers] = useState({}); 


  // Update answers based on the answerData and group by questionId
  // Fetch and group answers by questionId
  useEffect(() => {
    if (answerData?.getInviteeAnswers) {
      const answersByQuestionId = answerData.getInviteeAnswers.reduce((acc, val) => {
        const { questionId, title } = val;
        if (!acc[questionId]) {
          acc[questionId] = [];
        }
        acc[questionId].push(title);
        return acc;
      }, {});
      setOrginalAnswers(answersByQuestionId);
    }
  }, [answerData]);

  // Update the current item's options based on the current question's answers
  useEffect(() => {
    if (Array.isArray(orginalAnswers[currentItem.id])) {
      setAnswers(orginalAnswers[currentItem.id].filter(answer => answer.trim() !== ""));
    } else {
      setAnswers([]); // fallback to empty array if orginalAnswers is not an array
    }
  }, [orginalAnswers, currentItem?.id]);

  // Sync answers with the currentItem when answers change
  useEffect(() => {
    if (answers.length) {
      setCurrentItem(prev => ({
        ...prev,
        options: answers.filter(answer => answer.trim() !== "")
      }));
    }
  }, [answers]);


  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    const reorderedItems = Array.from(items);
    const [reorderedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, reorderedItem);
    setItems(reorderedItems);
  };

  const handleItemChange = (e) => {
    const { name, value, checked, type: inputType } = e.target;

    setCurrentItem((prev) => {
      const updatedItem = {
        ...prev,
        [name]: inputType === 'checkbox' ? checked : value,
      };
      return updatedItem;
    });
  };



  const handleCancelEdit = () => {
    setCurrentUpdateMode(false)
    setEditIndex(-1);
    setCurrentItem(defaultBookingTypeState);
    setCurrentUpdateMode(false)
  };

  const handleMenuClick = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowIndex(null);
  };

  const handleMenuAction = (action) => {
    if (action === 'edit') {
      setShowEntryForm(false);
      setEditIndex(selectedRowIndex);
      setCurrentUpdateMode(true)
      const cItem = items[selectedRowIndex];
      // Set currentItem with necessary updates in one step
      setCurrentItem((prev) => ({
        ...prev,
        ...cItem, // Spread the selected item first
      }));

      // if (cItem?.id && orginalAnswers[cItem.id]) {
      //   setAnswers(orginalAnswers[cItem.id].filter((answer) => answer.trim() !== ""));
      // }


    } else if (action === 'delete') {
      // Ask for confirmation before deletion
      const confirmDelete = window.confirm('Are you sure you want to delete this item?');

      if (confirmDelete) {
        const itemToDelete = items[selectedRowIndex]; // Get the item that will be deleted

        // Filter out the deleted item
        const updatedItems = items.filter((_, index) => index !== selectedRowIndex);

        // Call the delete mutation with the correct item ID
        deleteInviteeQuestion({
          variables: {
            id: itemToDelete.id  // Use the ID of the item that is being deleted
          }
        });

        // Update the state to reflect the deletion
        setItems(updatedItems);
      }
    }

    handleMenuClose();
  };


  const handleSaveEdit = () => {
    setCurrentUpdateMode(false)
    setShowEntryForm(false);
    addInviteeQuestion({
      variables: {
        ...currentItem, // Spread all properties of currentItem
        questionId: parseInt(items[editIndex].id), // Update questionId
      },
    })
      .then(async (response) => {
        // After the mutation succeeds, get the returned item with the new ID from the response
        const { __typename, ...rest } = response.data.addInviteeQuestion;
        // Update the items state with the new item (now including the correct ID)
        const updatedItemsWithNewId = items.map((item, index) =>
          index === editIndex ? { ...item, ...rest } : item
        );

        setItems(updatedItemsWithNewId); // Update the items state with the correct ID
        setEditIndex(-1); // Reset edit index
        setCurrentItem(defaultBookingTypeState); // Reset the edited item
        const oAnswers = orginalAnswers[currentItem.id]
        oAnswers.options = currentItem.options
        setOrginalAnswers({
          ...orginalAnswers, // keep the existing answers
          [currentItem.id]: currentItem.options // update the specific item
        });
        await refetch()
      })
      .catch((error) => {
        console.error("Error saving the item:", error);
      });
  };

  const handleAddItem = () => {
    setCurrentUpdateMode(false)
    setShowEntryForm(false);
    if (currentItem.title.trim() && currentItem.type) {
      // Perform the mutation to add the new item
      addInviteeQuestion({
        variables: {
          ...currentItem
        }
      })
        .then(async (response) => {
          // Get the new item with the id from the response
          const { __typename, ...rest } = response.data.addInviteeQuestion;

          // Add the new item to the local state (with the id returned from the backend)
          setItems((prevItems) => [...prevItems, rest]);

          setOrginalAnswers(prevAnswers => ({
            ...prevAnswers,              // Spread the previous answers
            [rest.id]: currentItem.options // Add the new answer (for the new id)
          }));
          // Clear the form and hide the entry form
          setCurrentItem(defaultBookingTypeState);
          setShowEntryForm(false);
          await refetch()
        })
        .catch((error) => {
          console.error("Error adding item:", error);
        });
    }
  };



  const handleCancelAddItem = () => {
    setCurrentUpdateMode(false)
    setCurrentItem(defaultBookingTypeState);
    setShowEntryForm(false);
  };

  const toggleEntryForm = () => {
    setCurrentUpdateMode(true)
    setShowEntryForm((prev) => !prev);
    setAnswers(["", ""]);
  };


  // Handle answer option changes
  const handleAnswerChange = (index, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index] = value;
    setAnswers(updatedAnswers);

  };


  // Handle answer option deletion
  function handleAnswerDeleted(index) {
    const updatedAnswers = [...answers];
    updatedAnswers.splice(index, 1);
    setAnswers(updatedAnswers);
  }


  const specialTitles = ["Name", "Phone Number"];
  const isSpecialTitle = (title) => specialTitles.includes(title);

  return (
    <CustomDialog openDialog={showInviteeQuestions} setOpenDialog={setShowInviteeQuestions}>
      <Box p={2}>
        <Typography variant="h5">Invitee Questions</Typography>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <Grid container spacing={2} mt={3} ref={provided.innerRef} {...provided.droppableProps}>
                {/* Header row */}
                <Grid container spacing={1} alignItems="center" mt={1}>
                  <Grid item xs={1} />
                  <Grid item xs={3}>
                    <Typography variant="subtitle1" fontWeight="bold">Question</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="subtitle1" fontWeight="bold">Type</Typography>
                  </Grid>
                  {/* Align "Required" header text to match checkbox position */}
                  <Grid item xs={3} display="flex" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">Required</Typography>
                  </Grid>
                  <Grid item xs={2} />
                </Grid>

                {/* Draggable items */}
                {items.map((item, index) => (
                  <Draggable key={item.id} draggableId={index.toString()} index={index}>
                    {(provided) => (
                      <Grid container spacing={2} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} alignItems="center">
                        <Grid item xs={1}>
                          <DragIndicatorIcon />
                        </Grid>
                        {editIndex === index ? (
                          <Grid item xs={12}>
                            <Box display="flex" flexDirection="column" gap={1}>
                              <TextField
                                name="title"
                                label="Question"
                                value={currentItem.title}
                                onChange={handleItemChange}
                                fullWidth
                                variant="outlined"
                                disabled={currentItem.title.toLowerCase() === "email"} 
                                multiline
                              />

                              <Select
                                name="type"
                                value={currentItem.type}
                                onChange={handleItemChange}
                                fullWidth
                                variant="outlined"
                              >
                                {types.map((option, idx) => (
                                  <MenuItem key={idx} value={option.text}>
                                    {option.icon} {option.text}
                                  </MenuItem>
                                ))}
                              </Select>
                              <Box display="flex" alignItems="center">
                                <Checkbox
                                  name="required"
                                  checked={currentItem.required}
                                  onChange={handleItemChange}
                                />
                                <Typography variant="body1">Required</Typography>
                              </Box>
                              {/* Show warning message if email is not required */}
                              {currentItem.title.toLowerCase() === "email" && !currentItem.required && (
                                <Typography color="warning.main" fontSize="0.85rem" sx={{ ml: 1, mt: 1 }}> 
                                    ⚠️ Leaving email optional could result in bogus or anonymous bookings. Name alone might not be enough.
                                    <br />
                                    If no email is provided, end users won’t be able to track their booked events.
                                  </Typography> 
                              )}

                              <AnswerOptions
                                types={types}
                                answers={answers}
                                setAnswers={setAnswers}
                                item={currentItem}
                                handleAnswerChange={handleAnswerChange}
                                handleAnswerDeleted={handleAnswerDeleted}
                              />
                              <Box display="flex" gap={1} mt={1}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={handleSaveEdit}
                                  startIcon={<SaveIcon />}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  onClick={handleCancelEdit}
                                  startIcon={<CancelIcon />}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </Box>
                          </Grid>
                        ) : (
                          <>
                            <Grid item xs={3}>
                              <Typography variant="body1">{item.title}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body1">{item.type}</Typography>
                            </Grid>
                            {/* Align checkbox with "Required" header text */}
                            <Grid item xs={3} display="flex" alignItems="center">
                              <Checkbox checked={item.required} disabled />
                            </Grid>
                            <Grid item xs={2} display="flex" justifyContent="space-between" alignItems="center">
                              <IconButton onClick={(e) => handleMenuClick(e, index)}>
                                {!isSpecialTitle(item.title) && (
                                  <IconButton onClick={(e) => handleMenuClick(e, index)}>
                                    <MoreVertIcon />
                                  </IconButton>
                                )}
                              </IconButton>
                              <Menu anchorEl={anchorEl} open={Boolean(anchorEl) && selectedRowIndex === index} onClose={handleMenuClose}>
                                <MenuItem onClick={() => handleMenuAction('edit')}>Edit</MenuItem>
                                <MenuItem onClick={() => handleMenuAction('delete')}>Delete</MenuItem>
                              </Menu>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Grid>

            )}
          </Droppable>
        </DragDropContext>

        <Box mt={3} display="flex" justifyContent="center">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={currentUpdateMode}  // Disable if any of the conditions are true
            onClick={toggleEntryForm}>
            Add Question
          </Button>
        </Box>

        {showEntryForm && (
          <Box mt={2} p={2} border="1px solid #ddd" borderRadius="8px">
            <Typography variant="h6">New Question</Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                name="title"
                label="Question"
                value={currentItem.title}
                onChange={handleItemChange}
                fullWidth
                variant="outlined"
                multiline
              />

              <FormControl fullWidth variant="outlined">
                <InputLabel id="question-answer-type-label">Question answer type</InputLabel>
                <Select
                  name="type"
                  labelId="question-answer-type-label"  // Links the label to the Select
                  label="Question answer type"          // Label text
                  value={currentItem.type}
                  onChange={handleItemChange}
                >
                  {types.map((option, idx) => (
                    <MenuItem key={idx} value={option.text}>
                      {option.icon} {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box display="flex" alignItems="center">
                <Checkbox
                  name="required"
                  checked={currentItem.required}
                  onChange={handleItemChange} 
                />
                <Typography variant="body1">Required</Typography>
              </Box>

              <AnswerOptions
                types={types}
                answers={answers}
                setAnswers={setAnswers}
                item={currentItem}
                handleAnswerChange={handleAnswerChange}
                handleAnswerDeleted={handleAnswerDeleted}
              />

              <Box display="flex" gap={1} mt={1} mb={4}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddItem}
                  startIcon={<SaveIcon />}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancelAddItem}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </CustomDialog>
  );
};

const AnswerOptions = ({ types, answers, setAnswers, item, handleAnswerChange, handleAnswerDeleted }) => {
  const hasOptions = types.find((val) => item.type === val.text)?.options;

  if (!hasOptions) return null;

  const handleAddAnswer = () => {
    const updatedAnswers = [...answers, ""];
    setAnswers(updatedAnswers);
  };

  return (
    <>
      <Typography>Answers</Typography>
      <Typography className="font2">
        Invitee can select one or many of the following:
      </Typography>
      <div
        style={{ gap: "15px", padding: "15px 0" }}
        className="flex column"
      >
        {answers?.map((val, ind) => (
          <div className="flex align" style={{ gap: "10px" }} key={ind}>
            <OutlinedInput
              fullWidth
              placeholder={"Option " + (ind + 1)}
              value={val}
              onChange={(e) => handleAnswerChange(ind, e.target.value)}
            />
            {answers.length > 1 && (
              <IconButton
                onClick={() => handleAnswerDeleted(ind)}
                style={{ width: "45px", height: "45px" }}
              >
                <Delete />
              </IconButton>
            )}
          </div>
        ))}
      </div>
      <Link
        style={{ cursor: "pointer", padding: "15px 0" }}
        onClick={handleAddAnswer}
      >
        Add another answer
      </Link>
    </>
  );
};

export default InviteeQuestions;
